import { cn } from "@/lib/utils";
import { ReactNode, useLayoutEffect, useRef, useState } from "react";

export type SegmentedOption<T extends string> = {
  value: T;
  /** Free-form so a segment can carry more than a word, e.g. an offer badge. */
  label: ReactNode;
};

type Variant = "primary" | "secondary";
type Track = "panel" | "page";

/*
  Primary paints the pill as the primary Button does: the same fill token and
  the same gradient border, so a selected segment and a button read as one
  system. Secondary is the quiet in-track highlight.
*/
const pillClasses: Record<Variant, string> = {
  primary: cn(
    "border-gradient shadow-button-new",
    "[--border-gradient-fill:var(--color-primary)]",
    "[--border-gradient-from:var(--color-primary-border)]",
    "[--border-gradient-to:var(--color-primary)]",
  ),
  secondary: "bg-neutral-700/70",
};

const selectedTextClasses: Record<Variant, string> = {
  primary: "text-on-primary",
  secondary: "text-white",
};

/*
  What the pill slides along, kept apart from the pill itself: the two vary
  independently, and a control on a 2.0 surface still wants a primary segment.
  `panel` is the 1.0 blue-tinted slate; `page` is the 2.0 ramp, sunk one step
  below the surface it sits on so the track reads as a groove.
*/
const trackClasses: Record<Track, string> = {
  panel: "border-neutral-700/70 bg-surface",
  page: "border-edge-strong bg-background",
};

/**
 * A row of mutually exclusive choices, each its own target, with one pill
 * that slides between them. The movement is what says they are alternatives.
 *
 * Segments size to their own text: "Transfer Market" is far wider than "Both",
 * and padding the short ones out to match only makes the control big. So the
 * pill is measured off the selected button rather than assumed to be an even
 * share of the track, and re-measured on resize since the widths move with the
 * font and the viewport.
 */
export const SegmentedControl = <T extends string>({
  options,
  value,
  onChange,
  label,
  variant = "secondary",
  track = "panel",
  className,
}: {
  options: SegmentedOption<T>[];
  /** null: nothing chosen yet, so no pill shows until the first pick. */
  value: T | null;
  onChange: (value: T) => void;
  /** Names the group for screen readers; never rendered. */
  label: string;
  /** How the selected segment is painted. */
  variant?: Variant;
  /** Which generation of surface the control is sitting on. */
  track?: Track;
  className?: string;
}) => {
  const buttons = useRef(new Map<T, HTMLButtonElement>());
  const [pill, setPill] = useState<{ left: number; width: number } | null>(
    null,
  );
  const pillRef = useRef<HTMLSpanElement>(null);
  // Transitions switch on only after the pill has landed on its first segment,
  // so mounting with the last option selected does not slide in from the left.
  // Landed means the browser has drawn it there: switched on from a passive
  // effect, the transition could beat the browser to the pill's first spot,
  // and the pill then slid in from the left edge anyway.
  const [settled, setSettled] = useState(false);
  useLayoutEffect(() => {
    if (!pill || settled) return;
    // Reading layout makes the browser place the pill now, transition still off
    pillRef.current?.getBoundingClientRect();
    setSettled(true);
  }, [pill, settled]);

  useLayoutEffect(() => {
    const active = value === null ? undefined : buttons.current.get(value);
    if (!active) {
      setPill(null);
      setSettled(false);
      return;
    }
    const measure = () =>
      // Bailing when the numbers are unchanged keeps a caller that builds its
      // options inline from re-rendering this on a loop.
      setPill((prev) =>
        prev?.left === active.offsetLeft && prev?.width === active.offsetWidth
          ? prev
          : { left: active.offsetLeft, width: active.offsetWidth },
      );
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(active);
    return () => observer.disconnect();
  }, [value, options.length]);

  return (
    <div
      role="radiogroup"
      aria-label={label}
      className={cn(
        "relative flex items-center gap-1 rounded-full border p-1",
        trackClasses[track],
        className,
      )}
    >
      {/* Two spans: the outer one slides, the inner one carries the paint.
          border-gradient sets position: relative, which would fight absolute. */}
      <span
        ref={pillRef}
        aria-hidden
        style={
          pill
            ? { transform: `translateX(${pill.left}px)`, width: pill.width }
            : undefined
        }
        className={cn(
          "pointer-events-none absolute inset-y-1 left-0",
          settled && "transition-[transform,width] duration-300 ease-out",
          !pill && "opacity-0",
        )}
      >
        <span
          className={cn("block size-full rounded-full", pillClasses[variant])}
        />
      </span>
      {options.map((option) => (
        <button
          key={option.value}
          ref={(node) => {
            if (node) buttons.current.set(option.value, node);
            else buttons.current.delete(option.value);
          }}
          type="button"
          role="radio"
          aria-checked={option.value === value}
          onClick={() => onChange(option.value)}
          className={cn(
            /* Tighter on phones, matching the native app's control; the variants
               are min-width, so the larger padding starts at touch-device. */
            "relative z-10 flex items-center gap-2 whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-semibold transition-colors touch-device:px-4 touch-device:text-sm",
            option.value === value
              ? selectedTextClasses[variant]
              : "text-foreground-muted hover:text-white",
          )}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
};
